/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import ProductBadges from "./ProductBadges";
import { deleteFromWishlist, postAddToWishlist } from "../Utils/api-service";
import {
  addToWishlist,
  getWishlist,
  removeFromWishlist,
} from "../Utils/wishlist-service";
import {
  buildWishlistUiStateMap,
  clearWishlistAnimateInMap,
  getWishlistClassName,
  toggleWishlistInMap,
} from "../Utils/wishlist-ui";
import { showToast } from "../Utils/toast-service";
import "./ProductAutoScroll.css";

const MIN_LOOP_SLIDES = 6;

const AUTOSCROLL_OPTIONS = {
  speed: 0.75,
  startDelay: 800,
  stopOnInteraction: false,
  stopOnMouseEnter: true,
};

function buildLoopSlides(products) {
  if (!products?.length) return [];

  const copies = Math.max(2, Math.ceil(MIN_LOOP_SLIDES / products.length));
  const slides = [];

  for (let copy = 0; copy < copies; copy += 1) {
    products.forEach((product, index) => {
      slides.push({
        product,
        slideKey: `${product.id}__${copy}__${index}`,
      });
    });
  }

  return slides;
}

function ProductCard({
  product,
  showPrice,
  onClick,
  imageSrc,
  imageAlt,
  titleId,
  badgeFallback,
  showWishlist,
  wishlistUiState,
  onToggleWishlist,
}) {
  return (
    <div onClick={onClick} className="product-box ratio_square">
      <div className="img-part">
        <a>
          <img
            src={imageSrc || "assets/images/products/1000.jpg"}
            alt={imageAlt || ""}
            className="img-fluid bg-img"
            draggable={false}
          />
        </a>
        {showWishlist && (
          <div
            className={getWishlistClassName(wishlistUiState)}
            onClick={(event) => onToggleWishlist(event, product)}
            role="button"
            tabIndex={0}
            aria-label="Toggle wishlist"
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                onToggleWishlist(event, product);
              }
            }}
          >
            <i className="iconly-Heart icli"></i>
            <i className="iconly-Heart icbo"></i>
            {wishlistUiState?.animate && (
              <div className="effect-group">
                <span className="effect"></span>
                <span className="effect"></span>
                <span className="effect"></span>
                <span className="effect"></span>
                <span className="effect"></span>
              </div>
            )}
          </div>
        )}
        {product?.totalInventory > 0 ? (
          <ProductBadges tags={product?.tags} fallback={badgeFallback} rotate />
        ) : (
          <label>out of stock</label>
        )}
      </div>
      <div className="product-content">
        <a>
          <h4 id={titleId}>{product?.title}</h4>
        </a>
        {showPrice && (
          <div className="price">
            {product?.variants?.nodes?.length > 1 ? (
              <h4 id={titleId}>
                ₹{product?.priceRange?.minVariantPrice?.amount}
                <span> onwards </span>
              </h4>
            ) : (
              <h4 id={titleId}>
                ₹{product?.priceRange?.minVariantPrice?.amount}
              </h4>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductAutoScroll({
  products,
  showPrice,
  onProductClick,
  getImageSrc = (product) => product?.featuredImage?.src,
  getImageAlt = (product) => product?.featuredImage?.altText || "",
  titleId,
  badgeFallback,
  showWishlist = false,
  wishlistSource = "HOME",
}) {
  const [reducedMotion] = React.useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  const [wishlistState, setWishlistState] = React.useState(() =>
    buildWishlistUiStateMap(getWishlist()),
  );

  const loopSlides = useMemo(() => buildLoopSlides(products), [products]);
  const shouldAutoScroll = !reducedMotion && products.length > 1;

  const plugins = useMemo(
    () => (shouldAutoScroll ? [AutoScroll(AUTOSCROLL_OPTIONS)] : []),
    [shouldAutoScroll],
  );

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: true,
      containScroll: false,
      skipSnaps: true,
    },
    plugins,
  );

  const toggleWishlist = async (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    if (!product?.id) return;

    const isActive = Boolean(wishlistState[product.id]?.active);
    setWishlistState((prev) => toggleWishlistInMap(prev, product.id));

    if (isActive) {
      removeFromWishlist(product.id);
      const response = await deleteFromWishlist({
        shopifyProductId: product.id,
      });
      if (response?.status !== 200 || response?.data?.removed === false) {
        setWishlistState((prev) => ({
          ...prev,
          [product.id]: buildWishlistUiStateMap([product.id])[product.id],
        }));
        addToWishlist(product.id);
      }
      return;
    }

    addToWishlist(product.id);
    const response = await postAddToWishlist({
      shopifyProductId: product.id,
      source: wishlistSource,
    });

    if (response?.status !== 201 && response?.status !== 200) {
      setWishlistState((prev) => toggleWishlistInMap(prev, product.id));
      removeFromWishlist(product.id);
      return;
    }

    showToast("Added to wishlist");

    setTimeout(() => {
      setWishlistState((prev) => clearWishlistAnimateInMap(prev, product.id));
    }, 700);
  };

  if (!products?.length) return null;

  const renderCard = (product) => (
    <ProductCard
      product={product}
      showPrice={showPrice}
      onClick={() => onProductClick(product.id)}
      imageSrc={getImageSrc(product)}
      imageAlt={getImageAlt(product)}
      titleId={titleId}
      badgeFallback={badgeFallback}
      showWishlist={showWishlist}
      wishlistUiState={wishlistState[product.id]}
      onToggleWishlist={toggleWishlist}
    />
  );

  if (products.length === 1) {
    return (
      <div className="product-autoscroll product-autoscroll--single">
        <div className="product-autoscroll__slide">
          {renderCard(products[0])}
        </div>
      </div>
    );
  }

  return (
    <div className="product-autoscroll" ref={emblaRef}>
      <div className="product-autoscroll__container">
        {loopSlides.map(({ product, slideKey }) => (
          <div className="product-autoscroll__slide" key={slideKey}>
            {renderCard(product)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProductAutoScroll;
